// Pause-control page — per-connector status + toggles. Backend's
// `/api/connectors/{id}/pause-status` returns `{ all_paused, features }`:
// `all_paused` is the near-plugins `ALL` master switch; `features` is the
// per-feature *individual* paused state. Pause/unpause endpoints return an
// UnsignedTxsResponse the connected NEAR wallet signs.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  ApiClientError,
  listConnectors,
  pauseFeature as apiPauseFeature,
  pauseStatus,
  unpauseFeature as apiUnpauseFeature,
} from "@/api/client";
import { useNearWallet } from "@/wallet/near";
import { friendlyWalletError } from "@/lib/walletError";
import type { ConnectorId, ConnectorInfo } from "@/types/dto";

/** near-plugins Pausable wildcard — pausing this one key pauses every feature;
 *  unpausing it lifts the master pause. The backend surfaces its state as the
 *  `all_paused` flag (not a feature row), so the page can offer a single
 *  "Pause all" / "Unpause all" toggle. */
const PAUSE_ALL_KEY = "ALL";

export function PausePage() {
  const near = useNearWallet();
  const connectorsQ = useQuery({
    queryKey: ["connectors"],
    queryFn: ({ signal }) => listConnectors(signal),
  });

  return (
    <section className="space-y-8" data-testid="pause-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Pause control</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Per-feature pause/unpause for every configured connector.
            Operator wallet signs every change.
          </p>
        </div>
        {!near.account && (
          <span
            className="rounded-md border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/30 px-3 py-1 text-xs text-amber-800 dark:text-amber-200"
            data-testid="pause-need-wallet"
          >
            No wallet connected — connect first.
          </span>
        )}
      </header>

      {connectorsQ.isLoading && (
        <div className="text-sm text-slate-600 dark:text-slate-400" data-testid="connectors-loading">
          Loading connectors…
        </div>
      )}
      {connectorsQ.isError && (
        <div
          className="rounded-md border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300"
          data-testid="connectors-error"
        >
          Failed to load connectors:{" "}
          {(connectorsQ.error as ApiClientError | Error).message}. Pause status is
          unknown — retry once the backend is reachable.
          <button
            type="button"
            onClick={() => void connectorsQ.refetch()}
            className="ml-2 rounded-md border border-red-300 dark:border-red-800/60 px-2 py-0.5 text-xs hover:bg-red-50 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      )}
      {connectorsQ.isSuccess && connectorsQ.data.length === 0 && (
        <div className="text-sm text-slate-500" data-testid="connectors-empty">
          No connectors configured.
        </div>
      )}

      {(connectorsQ.data ?? []).map((c) => (
        <ConnectorCard key={c.id} connector={c} canSign={!!near.account} />
      ))}
    </section>
  );
}

function ConnectorCard({
  connector,
  canSign,
}: {
  connector: ConnectorInfo;
  canSign: boolean;
}) {
  const qc = useQueryClient();
  const near = useNearWallet();
  const statusQ = useQuery({
    queryKey: ["pause-status", connector.id],
    queryFn: ({ signal }) => pauseStatus(connector.id, signal),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: async ({
      feature,
      pause,
    }: {
      feature: string;
      pause: boolean;
    }) => {
      setBusy(feature);
      setError(null);
      const resp = pause
        ? await apiPauseFeature(connector.id, feature)
        : await apiUnpauseFeature(connector.id, feature);
      const first = resp.unsignedTxs[0];
      if (!first) throw new Error("backend returned no transactions");
      return near.signAndSend(first);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pause-status", connector.id] });
    },
    onError: (e) =>
      setError(e instanceof ApiClientError ? e.message : friendlyWalletError(e)),
    onSettled: () => setBusy(null),
  });

  const features = statusQ.data?.features ?? {};
  const sortedNames = Object.keys(features).sort();
  const allPaused = statusQ.data?.all_paused ?? false;

  // The master switch is a single `pa_(un)pause_feature({"key":"ALL"})` tx (one
  // signature) rather than one tx per feature. The same button pauses or
  // unpauses everything depending on the current `all_paused` state.
  const MASTER_BUSY = "__all__";
  const masterToggle = useMutation({
    mutationFn: async () => {
      setBusy(MASTER_BUSY);
      setError(null);
      const resp = allPaused
        ? await apiUnpauseFeature(connector.id, PAUSE_ALL_KEY)
        : await apiPauseFeature(connector.id, PAUSE_ALL_KEY);
      const first = resp.unsignedTxs[0];
      if (!first) throw new Error("backend returned no transactions");
      return near.signAndSend(first);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pause-status", connector.id] });
    },
    onError: (e) =>
      setError(e instanceof ApiClientError ? e.message : friendlyWalletError(e)),
    onSettled: () => setBusy(null),
  });

  // Disable while a tx is in flight. `busy` is set inside the async mutationFn,
  // so it isn't truthy synchronously on click; also gate on React Query's
  // synchronously-set `isPending` to close the rapid double-click window.
  const anyPending = busy !== null || toggle.isPending || masterToggle.isPending;

  return (
    <article
      className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      data-testid={`connector-card-${connector.id}`}
    >
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div>
          <div className="text-slate-900 dark:text-slate-100 font-medium">{connector.label}</div>
          <div className="font-mono text-xs text-slate-500">{connector.contract}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void statusQ.refetch()}
            className="rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid={`refresh-${connector.id}`}
          >
            Refresh
          </button>
          <button
            type="button"
            disabled={!canSign || anyPending}
            onClick={() => masterToggle.mutate()}
            title={
              allPaused
                ? "Unpauses the `ALL` wildcard — lifts the master pause (individually-paused features stay paused)"
                : "Pauses everything via the single `ALL` key"
            }
            className={clsx(
              "rounded-md border px-3 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40",
              allPaused
                ? "border-near-green/60 text-near-green-dark dark:text-near-green hover:bg-near-green/15"
                : "border-rose-400 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/60",
            )}
            data-testid={`pause-all-${connector.id}`}
          >
            {busy === MASTER_BUSY ? "Working…" : allPaused ? "Unpause all" : "Pause all"}
          </button>
        </div>
      </header>
      {statusQ.isError && (
        <div className="px-4 py-3 text-xs text-red-700 dark:text-red-300" data-testid={`error-${connector.id}`}>
          {(statusQ.error as ApiClientError | Error).message}
        </div>
      )}
      {allPaused && (
        <div
          className="border-b border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-2 text-xs text-rose-700 dark:text-rose-200"
          data-testid={`all-paused-banner-${connector.id}`}
        >
          Everything is paused via the <code className="font-mono">ALL</code> master switch. Per-feature
          toggles below change the underlying state that applies once you unpause all.
        </div>
      )}
      {sortedNames.length === 0 && statusQ.isSuccess && (
        <div className="px-4 py-3 text-xs text-slate-500">
          No pausable features reported by this connector.
        </div>
      )}
      <ul>
        {sortedNames.map((feature) => {
          // `features[feature]` is the *individual* paused state. A feature can
          // be individually active yet effectively paused because `allPaused`
          // (the ALL wildcard) is set — show that as a distinct "via ALL" badge.
          const individuallyPaused = !!features[feature];
          const thisBusy = busy === feature;
          const badge = individuallyPaused
            ? { label: "paused", cls: "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-800/60" }
            : allPaused
              ? { label: "paused · via ALL", cls: "bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800/60" }
              : { label: "active", cls: "bg-near-green/15 text-near-green-dark dark:text-near-green border-near-green/40" };
          return (
            <li
              key={feature}
              className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800 px-4 py-2 first:border-t-0"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-800 dark:text-slate-200">{feature}</span>
                <span
                  className={clsx(
                    "rounded-md px-1.5 py-0.5 text-[10px] font-medium border",
                    badge.cls,
                  )}
                >
                  {badge.label}
                </span>
              </div>
              <button
                type="button"
                disabled={!canSign || anyPending}
                onClick={() => toggle.mutate({ feature, pause: !individuallyPaused })}
                title={
                  individuallyPaused
                    ? "Clears this feature's individual pause"
                    : allPaused
                      ? "Individually pauses this feature (stays paused after you unpause all)"
                      : "Pauses this feature"
                }
                className={clsx(
                  "rounded-md border px-3 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40",
                  individuallyPaused
                    ? "border-near-green/60 text-near-green-dark dark:text-near-green hover:bg-near-green/15"
                    : "border-rose-300 dark:border-rose-800/60 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/40",
                )}
                data-feature-toggle={feature}
              >
                {thisBusy ? "Working…" : individuallyPaused ? "Unpause" : "Pause"}
              </button>
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
    </article>
  );
}

// Re-export for type-narrowing in tests that import the named type.
export type { ConnectorId };
